import React from 'react';
import { AlertCircle } from 'lucide-react';
import { SqueletteCarte, SqueletteListe, SqueletteTexte } from '../../etats';

/* Briques communes aux onglets de suivi du coach (dossier, plan, seances,
   echanges, rapports). Memes jetons que le reste du back-office. */

export const champ =
  'w-full px-3 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--bg-main)] text-sm text-[var(--text-main)] focus:outline-none focus:border-[var(--accent)]';

export const boutonPrincipal =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--accent)] text-[#0A1128] text-[13px] font-semibold hover:opacity-90 transition-all disabled:opacity-50';

export const boutonSecondaire =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[var(--border)] text-[var(--text-main)] text-[13px] font-semibold hover:bg-[var(--bg-main)] transition-all disabled:opacity-50';

// De la plus pressante à la moins pressante : l'ordre des clés est l'ordre
// d'affichage. `fenetre` : le calendrier que propose le serveur
// (`interpretation.FENETRE_PAR_PRIORITE`).
export const PRIORITES = {
  critique: { label: 'Critique', fenetre: 'dans le mois', className: 'text-[var(--alerte)] bg-[var(--alerte-fond)] border-[var(--border)]' },
  urgent: { label: 'Urgent', fenetre: 'de J+15 à 3 mois', className: 'text-[var(--attention)] bg-[var(--attention-fond)] border-[var(--border)]' },
  important: { label: 'Important', fenetre: 'de 2 à 6 mois', className: 'text-[var(--text-bold)] bg-[var(--info-fond)] border-[var(--border)]' },
  utile: { label: 'Utile', fenetre: 'de 4 à 12 mois', className: 'text-[var(--text-main)] bg-[var(--bg-main)] border-[var(--border)]' },
};

export const STATUTS_ACTION = {
  non_demarre: 'Non démarré',
  en_cours: 'En cours',
  termine: 'Terminé',
  bloque: 'Bloqué',
};

export const STATUTS_SEANCE = {
  proposee: { label: 'Proposée', className: 'text-[var(--text-bold)] bg-[var(--info-fond)] border-[var(--border)]' },
  confirmee: { label: 'Confirmée', className: 'text-[var(--vert-fonce)] bg-[var(--vert-pale)] border-[var(--border)]' },
  a_replanifier: { label: 'À replanifier', className: 'text-[var(--attention)] bg-[var(--attention-fond)] border-[var(--border)]' },
  annulee: { label: 'Annulée', className: 'text-[var(--alerte)] bg-[var(--alerte-fond)] border-[var(--border)]' },
  realisee: { label: 'Réalisée', className: 'text-[var(--text-main)] bg-[var(--bg-main)] border-[var(--border)]' },
};

export function fmtDate(v) {
  return v ? new Date(v).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

export function fmtDateHeure(v) {
  return v
    ? new Date(v).toLocaleString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';
}

/* Valeur d'un <input type="datetime-local"> dans le fuseau du navigateur. */
export function versChampDateHeure(v) {
  if (!v) return '';
  const d = new Date(v);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function Pastille({ className = '', children }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-controle text-[12px] font-semibold border ${className}`}>
      {children}
    </span>
  );
}

export function Carte({ titre, action, children, className = '' }) {
  return (
    <section className={`bg-[var(--bg-card)] shadow-carte rounded-carte border border-[var(--border)] p-5 ${className}`}>
      {(titre || action) && (
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          {titre && <h3 className="text-base font-semibold text-[var(--text-bold)]">{titre}</h3>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/* Premier chargement d'un onglet : un squelette de la forme attendue
   (liste par défaut, deux cartes côte à côte, ou un bloc de texte). */
export function Chargement({ forme = 'liste' }) {
  if (forme === 'cartes') {
    return <div className="grid lg:grid-cols-2 gap-4 items-start"><SqueletteCarte /><SqueletteCarte /></div>;
  }
  if (forme === 'texte') {
    return (
      <div className="bg-[var(--bg-card)] shadow-carte rounded-carte border border-[var(--border)] p-5" role="status" aria-label="Chargement">
        <SqueletteTexte lignes={5} />
      </div>
    );
  }
  return <SqueletteListe lignes={3} />;
}

export function Erreur({ message }) {
  if (!message) return null;
  return (
    <div className="encart encart-alerte" role="alert">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

export function Vide({ icone: Icone, titre, texte, children }) {
  return (
    <div className="text-center py-10 px-4 text-[var(--text-muted)]">
      {Icone && <Icone className="w-10 h-10 mx-auto mb-2 opacity-30" />}
      <p className="font-semibold text-sm text-[var(--text-main)]">{titre}</p>
      {texte && <p className="text-xs mt-1 max-w-md mx-auto leading-relaxed">{texte}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

/* Texte multi-lignes (puces du referentiel, messages) sans HTML injecte. */
export function TexteLignes({ texte, className = '' }) {
  if (!texte) return null;
  return <p className={`whitespace-pre-line ${className}`}>{texte}</p>;
}
